import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';

const BlogPost = ({ 
  title, 
  content, 
  author, 
  date, 
  readTime, 
  category, 
  image,
  previousPost,
  nextPost 
}) => {
  const shareUrl = window.location.href;
  
  return (
    <div className="bg-white pt-16">
      {/* Back Navigation */}
      <section className="py-8 px-8 lg:px-16 bg-grey">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 text-mid-purple hover:text-light-pink transition-colors font-semibold">
            <ArrowLeft size={20} />
            Back to Blog
          </Link>
        </div>
      </section>

      {/* Hero Image & Title */}
      <section className="py-0">
        <div className="max-w-6xl mx-auto">
          <div className="relative h-96 overflow-hidden">
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            
            {/* Category Badge */}
            <div className="absolute top-8 left-8">
              <span className="px-4 py-2 rounded-full bg-light-pink text-white text-sm font-bold uppercase tracking-wider">
                {category}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              {title}
            </h1>

            {/* Meta Info */}
            <div className="flex items-center gap-6 mb-8 text-dark-grey">
              <div className="flex items-center gap-2">
                <User size={18} />
                <span className="font-semibold">{author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>{readTime}</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4">
              <span className="text-dark-grey font-semibold">Share:</span>
              <div className="flex gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-grey hover:bg-light-pink hover:text-white transition-all flex items-center justify-center"
                >
                  <Twitter size={18} />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-grey hover:bg-light-pink hover:text-white transition-all flex items-center justify-center"
                >
                  <Linkedin size={18} />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-grey hover:bg-light-pink hover:text-white transition-all flex items-center justify-center"
                >
                  <Facebook size={18} />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          {/* Navigation */}
          {(previousPost || nextPost) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex justify-between items-center mt-16 pt-8 border-t border-grey"
            >
              {previousPost ? (
                <Link to={`/blog/${previousPost.slug}`} className="flex-1 mr-4">
                  <div className="p-6 rounded-xl border border-grey hover:border-light-pink transition-all group">
                    <span className="text-sm text-dark-grey uppercase tracking-wider">Previous</span>
                    <h3 className="text-lg font-bold group-hover:text-light-pink transition-colors">
                      {previousPost.title}
                    </h3>
                  </div>
                </Link>
              ) : <div className="flex-1" />}

              {nextPost && (
                <Link to={`/blog/${nextPost.slug}`} className="flex-1 ml-4">
                  <div className="p-6 rounded-xl border border-grey hover:border-light-pink transition-all group text-right">
                    <span className="text-sm text-dark-grey uppercase tracking-wider">Next</span>
                    <h3 className="text-lg font-bold group-hover:text-light-pink transition-colors">
                      {nextPost.title}
                    </h3>
                  </div>
                </Link>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Author Bio */}
      <section className="py-16 px-8 lg:px-16 bg-grey">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 border border-grey">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-light-pink to-mid-purple flex items-center justify-center">
                <User size={32} className="text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">About {author}</h3>
                <p className="text-dark-grey leading-relaxed">
                  {author === 'Sarah Chen' && "Sarah is the CEO and founder of SYNCORE LLC with over 15 years of experience in digital transformation and business strategy."}
                  {author === 'Marcus Rodriguez' && "Marcus is our Chief Technology Officer, formerly a Lead Engineer at major tech companies, specializing in AI and machine learning."}
                  {author === 'Emily Watson' && "Emily is our Head of Design, a skilled UI/UX specialist with expertise in creating intuitive user experiences."}
                  {author === 'David Park' && "David is our Director of Strategy with an MBA from Stanford and over 10 years of consulting experience."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;